import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { PlannerService } from '../../../service/api/planner-service-v2';
import { normalizeSavePlanWorkTicketFields } from '../../../service/api/planner-save-plan-body';
const { PROGRAM_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'planner_v2';
// Feature flag to control whether we should immediately re-fetch planner data
// after a successful delete. When the backend does not persist deletes instantly
// (or the delete identifier differs from the fetch identifier), re-fetching can
// re-introduce the just-removed item. Keeping this false relies on our local
// state update which is already done in deleteByWorkTicketId.fulfilled.
const REFRESH_AFTER_DELETE = false;
export const INITIAL_STATE = {
  data: {
    channels: [],
    programs: [],
    elements: [],
  },
  // Track deleted program identifiers for this session so auto-refresh
  // or re-fetch does not re-introduce the deleted items if the backend
  // still returns them.
  deletedProgramIds: [],
  // Extra signature keys (like title/description) to guard against
  // servers that return different identifiers between endpoints
  deletedProgramKeys: [],
  deletedElementIds: [],
  loading: false,
  error: null,
  filters: {
    start: '',
    end: '',
     searchText: '',
    status: ''   // new
  },
  fetchWorkTicketPending: false,
  statusLog: [],
  statusLogLoading: false,
  statusLogError: null,
  completionStatus: {}, // Map of ProcessId to completion percentage
  completionStatusLoading: false,
};

/** API layers sometimes nest `message` as an object; never pass that through to React toasts. */
const coerceToUserMessage = (value, fallback = 'Failed to process request') => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') {
    const t = value.trim();
    return t || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const nested = value.message ?? value.Message ?? value.errorMessage ?? value.error;
    if (nested != null && nested !== value) {
      const inner = coerceToUserMessage(nested, fallback);
      if (inner !== fallback || typeof nested === 'string') return inner;
    }
    if (typeof value.title === 'string' && value.title.trim()) return value.title.trim();
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
};

const handleResponse = async (promise, rejectWithValue) => {
  try {
    const response = await promise;
    if (response.status === 200) {
      return response;
    } else {
      // Pass status along with error message for error handling
      const rawMsg =
        response.errorMessage?.message ??
        response.message ??
        response.errorMessage ??
        response.error;
      return rejectWithValue({
        status: response.status,
        message: coerceToUserMessage(rawMsg, 'Failed to process request'),
        errorMessage: response.errorMessage
      });
    }
  } catch (error) {
    // Check if error has status property
    const errorStatus = error?.status || error?.response?.status;
    return rejectWithValue({
      status: errorStatus || 500,
      message: coerceToUserMessage(error?.message, 'Failed to process request'),
      errorMessage: error
    });
  }
};

// Utility comparator used across reducers
const matchesId = (p, id) => {
  const pid = String(id);
  return [
    p.id,
    p.workTicketNumber,
    p.workTicketNo,
    p.workTicket,
    p.processIdExt,
  ].some(v => v !== undefined && v !== null && String(v) === pid);
};

const collectKeys = (obj) => {
  if (!obj) return [];
  const keys = [
    obj.id,
    obj.workTicketNumber,
    obj.workTicketNo,
    obj.workTicket,
    obj.processIdExt,
    obj.title,
    obj.description,
  ]
    .filter(v => v !== undefined && v !== null)
    .map(v => String(v));
  return Array.from(new Set(keys));
};

export const fetchPlanner = createAsyncThunk(
  'planner/fetchPlanner',
  async (filters, { rejectWithValue }) => {
    const isNilOrEmpty = v => v == null || (typeof v === 'string' && v.trim() === '');
    const toIsoLike = v => {
      if (isNilOrEmpty(v)) return null;
      const s = String(v).trim();
      // Accept yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00`;
      if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString().replace(/\.\d{3}Z$/, '');
    };

    const channelResponse = await handleResponse(PlannerService.getAllChannel(), rejectWithValue);
    // IMPORTANT: getAllTask backend can throw 500 if it receives empty/invalid date strings.
    // Only send plannedStartDate/plannedEndDate when we have valid values.
    const startIso = toIsoLike(filters?.start);
    const endIso = toIsoLike(filters?.end);
    const taskPayload = {};
    if (startIso) taskPayload.plannedStartDate = startIso;
    if (endIso) taskPayload.plannedEndDate = endIso;

    const taskResponse = await handleResponse(PlannerService.getAllTask(taskPayload), rejectWithValue);
    
    // Always normalize task payload shape so reducers never see `undefined`
    const taskData = taskResponse?.response;
    const normalizedTask = {
      programs: Array.isArray(taskData?.programs) ? taskData.programs : [],
      elements: Array.isArray(taskData?.elements) ? taskData.elements : [],
    };

    return [Array.isArray(channelResponse?.response) ? channelResponse.response : [], normalizedTask];
  },
);

// Fetch completion status after planner data is loaded
export const fetchPlannerWithCompletion = createAsyncThunk(
  'planner/fetchPlannerWithCompletion',
  async (filters, { dispatch, rejectWithValue }) => {
    // First fetch planner data
    const plannerResult = await dispatch(fetchPlanner(filters));
    // Then fetch completion status
    await dispatch(fetchProcessCompletionStatus());
    return plannerResult.payload;
  },
);

export const savePlan = createAsyncThunk('planner/savePlan', async (data, { rejectWithValue }) => {
  const payload = { ...data };
  normalizeSavePlanWorkTicketFields(payload);

  const userId = localStorage.getItem('UserId');
  if (userId != null && String(userId).trim() !== '') {
    payload.userId = String(userId).trim();
  } else {
    delete payload.userId;
    delete payload.UserId;
  }

  const idStr = payload.id != null ? String(payload.id).trim() : '';
  if (!/^\d+$/.test(idStr)) {
    const ticket = String(payload.title ?? '').trim();
    if (ticket) {
      payload.id = `W${ticket}`;
    }
  }

  if (payload.since != null) {
    payload.since = String(payload.since).replace(' ', 'T');
  }
  if (payload.till != null) {
    payload.till = String(payload.till).replace(' ', 'T');
  }

  return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
});

/** Same payload shape as savePlan; server expects placeholder cell, dates, and machine. */
export const archivePlan = createAsyncThunk('planner/archivePlan', async (data, { rejectWithValue }) => {
  const payload = { ...data };
  normalizeSavePlanWorkTicketFields(payload);

  const userId = localStorage.getItem('UserId');
  if (userId != null && String(userId).trim() !== '') {
    payload.userId = String(userId).trim();
  } else {
    delete payload.userId;
    delete payload.UserId;
  }

  const ticket = String(payload.title ?? '').trim();
  payload.id = ticket ? `W${ticket}` : payload.id;

  payload.channelUuid = '000';
  payload.machineId = '00';
  // SQL Server cannot convert 0000-00-00; invalid dates put SP in CATCH and return a grid without "Id",
  // which makes C# reader.GetOrdinal("Id") throw (API error message shows as "Id").
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  payload.since = start.toISOString().replace(/\.\d{3}Z$/, '');
  payload.till = end.toISOString().replace(/\.\d{3}Z$/, '');
  payload.since = payload.since.replace(' ', 'T');
  payload.till = payload.till.replace(' ', 'T');

  return handleResponse(PlannerService.archivePlan(payload), rejectWithValue);
});

export const updatePlan = createAsyncThunk(
  'planner/updatePlan',
  async (data, { rejectWithValue }) => {
    const payload = { ...data };
    normalizeSavePlanWorkTicketFields(payload);

    const userId = localStorage.getItem('UserId');
    if (userId != null && String(userId).trim() !== '') {
      payload.userId = String(userId).trim();
    } else {
      delete payload.userId;
      delete payload.UserId;
    }

    return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
  },
);

export const updateProcess = createAsyncThunk(
  'planner/updateProcess',
  async (data, { rejectWithValue }) => {
    return handleResponse(PlannerService.updateProcess(data), rejectWithValue);
  },
);

export const fetchByWorkTicketId = createAsyncThunk(
  'planner/fetchByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.getByWorkTicketId(id), rejectWithValue);
  },
);

/**
 * Fetch work ticket by id WITHOUT triggering the PROGRAM_ADD_EDIT modal.
 * Use this for drag/drop validation flows where we need the payload to decide UX.
 */
export const fetchByWorkTicketIdRaw = createAsyncThunk(
  'planner/fetchByWorkTicketIdRaw',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.getByWorkTicketId(id), rejectWithValue);
  },
);

export const fetchWorkTicketStatusLog = createAsyncThunk(
  'planner/fetchWorkTicketStatusLog',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.getWorkTicketStatusLog(id), rejectWithValue);
  },
);

export const deleteByWorkTicketId = createAsyncThunk(
  'planner/deleteByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.deleteByWorkTicketId(id), rejectWithValue);
  },
);

export const fetchProcessCompletionStatus = createAsyncThunk(
  'planner/fetchProcessCompletionStatus',
  async (_, { rejectWithValue }) => {
    return handleResponse(PlannerService.getProcessCompletionStatus(), rejectWithValue);
  },
);

// Open Processing Parameters document for a given work ticket number
export const openProcessingParameters = createAsyncThunk(
  'planner/openProcessingParameters',
  async (workTicketNumber, { rejectWithValue }) => {
    if (!workTicketNumber) {
      return rejectWithValue('No work ticket number available for Processing Parameter download.');
    }

    return handleResponse(
      PlannerService.getProcessingParametersUrl(workTicketNumber, '/ProcessingParameters'),
      rejectWithValue,
    );
  },
);


// Helper function to ensure state.data exists (mutates state for Immer compatibility)
const ensureStateData = (state) => {
  if (!state.data) {
    state.data = { ...INITIAL_STATE.data };
  }
  // Ensure arrays exist
  if (!Array.isArray(state.data.programs)) {
    state.data.programs = [];
  }
  if (!Array.isArray(state.data.elements)) {
    state.data.elements = [];
  }
  if (!Array.isArray(state.data.channels)) {
    state.data.channels = [];
  }
};

export const { reducer, actions } = createSlice({
    //new code(status filter)
//const slice = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    // setFromDate: (state, action) => {
    //   state.filters.fromDate = action.payload;
    // },
    // setToDate: (state, action) => {
    //   state.filters.toDate = action.payload;
    // },

    //new
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    removeElement: (state, action) => {
      ensureStateData(state);
      const id = String(action.payload);
      state.data.elements = (state.data.elements || []).map(element =>
        String(element.id) === id ? { ...element, deleted: true } : element,
      );
      if (!state.deletedElementIds?.includes(id)) {
        state.deletedElementIds?.push(id);
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPlanner.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanner.fulfilled, (state, action) => {
        ensureStateData(state);
        state.loading = false;
        state.data.channels = action.payload[0] || [];
        const incomingPrograms = action.payload[1]?.programs || [];
        
        // Filter out programs that were deleted during this session
        if (state.deletedProgramIds?.length > 0 || state.deletedProgramKeys?.length > 0) {
          state.data.programs = incomingPrograms.filter(p => {
            const candidateKeys = collectKeys(p);
            const idExcluded = state.deletedProgramIds.some(id => matchesId(p, id));
            const keyExcluded = candidateKeys.some(k => state.deletedProgramKeys?.includes(k));
            return !(idExcluded || keyExcluded);
          });
        } else {
          state.data.programs = incomingPrograms;
        }
        const incomingElements = action.payload[1]?.elements || [];
        const incomingElementIds = new Set(incomingElements.map(e => String(e.id)));
        
        // Preserve client-only elements not returned by getalltask (exclude deletedElementIds)
        const existingElements = state.data.elements || [];
        const manuallyCreatedElements = existingElements.filter(element => {
          const eid = String(element.id);
          const title = String(element.title || element.id);
          return (
            !incomingElementIds.has(eid) &&
            !state.deletedElementIds?.includes(eid) &&
            !state.deletedElementIds?.includes(title) &&
            !element.deleted
          );
        });
        
        // Merge incoming elements with manually created elements
        const mergedElements = [
          ...incomingElements
            .filter(element => {
              const eid = String(element.id);
              const title = String(element.title || element.id);
              return (
                !state.deletedElementIds?.includes(eid) && !state.deletedElementIds?.includes(title)
              );
            })
            .map(element => ({
              ...element,
              duration: '120',
              deleted: false,
            })),
          ...manuallyCreatedElements
        ];
        
        state.data.elements = mergedElements;
      })
      .addCase(fetchPlanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(savePlan.fulfilled, (state, action) => {
        if (action.payload?.response) {
          ensureStateData(state);
          const newProgram = action.payload.response;
          
          // Ensure programs array exists
          if (!Array.isArray(state.data.programs)) {
            state.data.programs = [];
          }
          
          // Add the new program to the programs array
          state.data.programs = [...state.data.programs, newProgram];
          
          // Collect all possible IDs and keys from the new program
          const programKeys = collectKeys(newProgram);
          const programIds = [
            newProgram.id,
            newProgram.workTicketNumber,
            newProgram.workTicketNo,
            newProgram.workTicket,
            newProgram.processIdExt,
            newProgram.title,
          ].filter(v => v !== undefined && v !== null).map(v => String(v));
          
          // Remove from deletedProgramIds using all possible IDs
          if (Array.isArray(state.deletedProgramIds)) {
            state.deletedProgramIds = state.deletedProgramIds.filter(id => {
              const idStr = String(id);
              return !programIds.some(pid => String(pid) === idStr || matchesId(newProgram, id));
            });
          }
          
          // Remove from deletedProgramKeys using all possible keys
          if (Array.isArray(state.deletedProgramKeys)) {
            state.deletedProgramKeys = state.deletedProgramKeys.filter(key => {
              return !programKeys.includes(String(key));
            });
          }
          
          // Remove from deletedElementIds - check all possible element IDs that might have been stored
          // This handles cases where element ID might match workTicketNumber, workTicketNo, title, or id
          if (Array.isArray(state.deletedElementIds)) {
            const possibleElementIdsToRemove = [
              newProgram.workTicketNumber,
              newProgram.workTicketNo,
              newProgram.workTicket,
              newProgram.title,
              newProgram.id,
            ].filter(v => v !== undefined && v !== null).map(v => String(v));
            
            state.deletedElementIds = state.deletedElementIds.filter(id => {
              const idStr = String(id);
              return !possibleElementIdsToRemove.some(eid => eid === idStr);
            });
          }
          
          // Remove the element from elements array if it exists (since it's now a program)
          // Also mark as deleted to ensure it doesn't show in Task board
          if (Array.isArray(state.data.elements)) {
            state.data.elements = state.data.elements.map(element => {
              const elementIdStr = String(element.id);
              const matches = programIds.some(pid => String(pid) === elementIdStr) || 
                             matchesId(newProgram, element.id);
              if (matches) {
                // Mark as deleted and remove from array
                return null;
              }
              return element;
            }).filter(element => element !== null);
          }
          
          // DO NOT add the element ID back to deletedElementIds here
          // We want the element to be able to reappear in the task bar if the program is deleted again
          // The element is already removed from the elements array above, so it won't show in task bar while it's a program
        }
      })
      .addCase(fetchByWorkTicketId.pending, state => {
        state.fetchWorkTicketPending = true;
      })
      .addCase(fetchByWorkTicketId.fulfilled, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(fetchByWorkTicketId.rejected, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(fetchWorkTicketStatusLog.pending, state => {
        state.statusLogLoading = true;
        state.statusLogError = null;
      })
      .addCase(fetchWorkTicketStatusLog.fulfilled, (state, action) => {
        state.statusLogLoading = false;
        // Handle different possible response structures
        const responseData = action.payload?.response || action.payload?.data?.response || action.payload?.data || [];
        state.statusLog = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(fetchWorkTicketStatusLog.rejected, (state, action) => {
        state.statusLogLoading = false;
        state.statusLogError = action.payload;
        state.statusLog = [];
      })
      .addCase(fetchProcessCompletionStatus.pending, state => {
        state.completionStatusLoading = true;
      })
      .addCase(fetchProcessCompletionStatus.fulfilled, (state, action) => {
        state.completionStatusLoading = false;
        // Convert array to object map for easy lookup by processId
        const completionMap = {};
        const responseData = action.payload?.response || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
        
        if (Array.isArray(responseData) && responseData.length > 0) {
          responseData.forEach((item) => {
            const processId = item.processId;
            if (processId !== undefined && processId !== null) {
              const processIdKey = String(processId);
              completionMap[processIdKey] = {
                plannedQuantity: item.plannedQuantity || 0,
                inventoryCount: item.inventoryCount || 0,
                completionPercentage: item.completionPercentage || 0,
              };
            }
          });
        }
        
        state.completionStatus = completionMap;
      })
      .addCase(fetchProcessCompletionStatus.rejected, (state, action) => {
        state.completionStatusLoading = false;
        state.completionStatus = {};
      })
      .addCase(deleteByWorkTicketId.fulfilled, (state, action) => {       
        ensureStateData(state);
        // Get the program ID from the action payload (the original request parameter)
        const programId = action.meta.arg;
        const response = action.payload?.response;
        const deletedProgram = state.data.programs?.find(p => matchesId(p, programId));

        // Ensure arrays are initialized
        if (!Array.isArray(state.data.programs)) state.data.programs = [];
        if (!Array.isArray(state.data.elements)) state.data.elements = [];
        if (!Array.isArray(state.deletedProgramIds)) state.deletedProgramIds = [];
        if (!Array.isArray(state.deletedProgramKeys)) state.deletedProgramKeys = [];
        if (!Array.isArray(state.deletedElementIds)) state.deletedElementIds = [];

        // Update programs from response if available
        // IMPORTANT: Filter out the deleted program from the API response to prevent it from reappearing
        if (response?.programs && Array.isArray(response.programs)) {
          // Filter out the deleted program by matching its ID directly
          // Also filter using deletedProgramIds to catch programs deleted earlier in this session
          state.data.programs = response.programs.filter(p => {
            // Exclude the program we just deleted
            if (deletedProgram && matchesId(p, programId)) {
              return false;
            }
            // Exclude programs deleted earlier in this session
            const candidateKeys = collectKeys(p);
            const idExcluded = state.deletedProgramIds.some(id => matchesId(p, id));
            const keyExcluded = candidateKeys.some(k => state.deletedProgramKeys?.includes(k));
            return !(idExcluded || keyExcluded);
          });
        } else if (deletedProgram) {
          // Fallback: Remove from programs if response doesn't include updated programs
          state.data.programs = state.data.programs.filter(p => !matchesId(p, programId));
        }

        // Remove stale task-board suppressions for this work ticket so elements from getalltask
        // show immediately (same as after full page reload). Do NOT push to deletedElementIds here —
        // that hid rows the API legitimately returns in `elements` after Process delete.
        if (deletedProgram) {
          const ticketKeys = [
            deletedProgram.workTicketNumber,
            deletedProgram.workTicketNo,
            deletedProgram.workTicket,
            deletedProgram.title,
            deletedProgram.id,
          ]
            .filter(v => v !== undefined && v !== null)
            .map(v => String(v).trim())
            .filter(Boolean);

          state.deletedElementIds = (state.deletedElementIds || []).filter(
            id => !ticketKeys.includes(String(id)),
          );

          if (Array.isArray(state.data.elements)) {
            const hide = new Set(ticketKeys);
            state.data.elements = state.data.elements.filter(e => {
              const eid = String(e.id);
              const title = String(e.title || e.id);
              return !hide.has(eid) && !hide.has(title);
            });
          }
        }

        // Update elements from response if available
        if (response?.elements && Array.isArray(response.elements)) {
          const incomingElements = response.elements;
          const incomingElementIds = new Set(incomingElements.map(e => String(e.id)));
          // Create a set of incoming element titles to check for duplicates (API might use "W" prefix)
          const incomingElementTitles = new Set(incomingElements.map(e => String(e.title || e.id)).filter(Boolean));
          
          // Preserve manually created elements that aren't in the API response
          const existingElements = state.data.elements || [];
          const manuallyCreatedElements = existingElements.filter(element => {
            const elementId = String(element.id);
            const elementTitle = String(element.title || element.id);
            return (
              !incomingElementIds.has(elementId) &&
              !incomingElementTitles.has(elementTitle) &&
              !state.deletedElementIds?.includes(elementId) &&
              !state.deletedElementIds?.includes(elementTitle) &&
              !element.deleted
            );
          });
          
          // Merge incoming elements with manually created elements and deduplicate by ID and title
          const mergedElementsMap = new Map();
          
          // First add incoming elements (prioritize API response)
          incomingElements
            .filter(element => {
              const eid = String(element.id);
              const title = String(element.title || element.id);
              return (
                !state.deletedElementIds?.includes(eid) && !state.deletedElementIds?.includes(title)
              );
            })
            .forEach(element => {
              const elementId = String(element.id);
              if (!mergedElementsMap.has(elementId)) {
                mergedElementsMap.set(elementId, {
                  ...element,
                  // Preserve duration from API (could be number in minutes), convert to string if needed
                  duration: element.duration ? String(element.duration) : '120',
                  deleted: false,
                });
              }
            });
          
          // Then add other manually created elements (only if not already present by ID or title)
          manuallyCreatedElements.forEach(element => {
            const elementId = String(element.id);
            const elementTitle = String(element.title || element.id);
            // Check if already exists by ID
            if (!mergedElementsMap.has(elementId)) {
              // Also check if any existing element has the same title
              const existsByTitle = Array.from(mergedElementsMap.values()).some(e => {
                const existingTitle = String(e.title || e.id);
                return existingTitle === elementTitle;
              });
              if (!existsByTitle) {
                mergedElementsMap.set(elementId, element);
              }
            }
          });
          
          state.data.elements = Array.from(mergedElementsMap.values());
        }

        // Remember this deletion for the rest of the session to prevent
        // auto-refresh from bringing the item back if the backend still
        // returns it.
        if (deletedProgram) {
          if (!state.deletedProgramIds.some(id => String(id) === String(programId))) {
            state.deletedProgramIds.push(String(programId));
          }

          const keysToStore = collectKeys(deletedProgram);
          keysToStore.forEach(k => {
            if (!state.deletedProgramKeys.includes(k)) {
              state.deletedProgramKeys.push(k);
            }
          });

        }
      })
      .addCase(updateProcess.fulfilled, (state, action) => {
        if (action.payload?.response) {
          ensureStateData(state);
          const updatedProcess = action.payload.response;
          state.data.programs = (state.data.programs || []).map(program =>
            program.id === updatedProcess.id ? updatedProcess : program,
          );
        }
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        if (action.payload?.response) {
          ensureStateData(state);
          const updatedPlan = action.payload.response;
          state.data.programs = (state.data.programs || []).map(program =>
            program.id === updatedPlan.id ? updatedPlan : program,
          );
        }
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchPlanner.rejected, savePlan.rejected, archivePlan.rejected, updateProcess.rejected),
  effect: (action, listenerApi) => {
    const raw =
      typeof action.payload === 'string'
        ? action.payload
        : action.payload?.message ?? action.payload?.errorMessage;
    const msg = coerceToUserMessage(raw, 'Something went wrong');
    listenerApi.dispatch(
      showToast({
        message: msg,
        type: 'danger',
      }),
    );
  },
});


redux.listener.startListening({
  matcher: isAnyOf(updatePlan.fulfilled),
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();
    const plannerState = state?.[STORE_KEY] || INITIAL_STATE;
    const filters = plannerState.filters || INITIAL_STATE.filters;
    listenerApi.dispatch(
      fetchPlanner({
        searchText: filters.searchText || '',
        status: filters.status || '',
        start: filters.start || '',
        end: filters.end || '',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(archivePlan.fulfilled),
  effect: (action, listenerApi) => {
    const taskId = action.meta.arg?.title;
    if (taskId !== undefined && taskId !== null) {
      listenerApi.dispatch(actions.removeElement(String(taskId)));
    }
    listenerApi.dispatch(
      showToast({
        message: 'Task archived successfully.',
        type: 'success',
      }),
    );
    const state = listenerApi.getState();
    const plannerState = state?.[STORE_KEY] || INITIAL_STATE;
    const filters = plannerState.filters || INITIAL_STATE.filters;
    listenerApi.dispatch(
      fetchPlanner({
        searchText: filters.searchText || '',
        status: filters.status || '',
        start: filters.start || '',
        end: filters.end || '',
      }),
    );
  },
});

if (REFRESH_AFTER_DELETE) {
  redux.listener.startListening({
    matcher: isAnyOf(deleteByWorkTicketId.fulfilled),
    effect: (action, listenerApi) => {
      const state = listenerApi.getState();
      const filters = state.planner.filters;
      listenerApi.dispatch(
        fetchPlanner({
          searchText: filters.searchText,
          status: filters.status,
          start: filters.start,
          end: filters.end,
        }),
      );
    },
  });
}

redux.listener.startListening({
  matcher: isAnyOf(fetchByWorkTicketId.fulfilled),
  effect: (action, listenerApi) => {
    // Log the payload we are about to pass into the ProgramAddEdit modal
    console.log('[PlannerV2] fetchByWorkTicketId fulfilled, opening PROGRAM_ADD_EDIT modal with data:', {
      rawPayload: action.payload,
      response: action.payload?.response,
    });
    listenerApi.dispatch(showModal(PROGRAM_ADD_EDIT, action.payload.response));
  },
});



// export const actions = slice.actions;
// export const { setFilters, setSearchText, removeElement } = slice.actions;
// const reducer = slice.reducer;

// redux.injectReducer(STORE_KEY, reducer);

// export default reducer;

redux.listener.startListening({
  matcher: isAnyOf(updateProcess.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(PROGRAM_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Plan updated successfully',
        type: 'success',
      }),
    );
    // Refresh the planner grid after modal updates
    const state = listenerApi.getState();
    const plannerState = state?.[STORE_KEY] || INITIAL_STATE;
    const filters = plannerState.filters || INITIAL_STATE.filters;
    listenerApi.dispatch(
      fetchPlanner({
        searchText: filters.searchText || '',
        status: filters.status || '',
        start: filters.start || '',
        end: filters.end || '',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
